import { z } from "zod";
import { Species } from "@spotpetinsurance/spot-types/entities/Species";
import { FirstNameSchema, LastNameSchema, MarketingSchema, PetSchema } from "@/shared/types/Quote.interface";
import { CA_MAX_AGE_CAT, CA_MAX_AGE_DOG } from "../utils/constants";
import { RegexUtils } from "@/shared/utils/RegexUtils";
import Strings from "@/shared/utils/Strings.constants";
import { UIUtils } from "@/shared/utils/UIUtils";

type ValidationResult = { success: true } | { success: false; error: string; field: string };

const validatePetAge = (data: { birthMonth?: string; birthYear?: string; species?: Species }): ValidationResult => {
    // Skip validation if we don't have complete date information
    if (!data.birthYear || !data.birthMonth) {
        return { success: true };
    }

    const birthYear = parseInt(data.birthYear, 10);
    const birthMonth = parseInt(data.birthMonth, 10);
    const today = new Date();
    const birthDate = new Date(birthYear, birthMonth - 1, 1); // First day of the month

    // Future date check
    if (birthDate > today) {
        return {
            success: false,
            error: "Birth date cannot be in the future.",
            field: "birthYear"
        };
    }

    // 8-week check (56 days)
    const diffTime = Math.abs(today.getTime() - birthDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 56) {
        return {
            success: false,
            error: Strings.ERRORS.PET_MIN_AGE,
            field: "birthMonth"
        };
    }

    // Calculate age
    let age = today.getFullYear() - birthYear;
    const hasBirthdayPassedThisYear = today.getMonth() + 1 > birthMonth || (today.getMonth() + 1 === birthMonth && today.getDate() >= 1);
    if (!hasBirthdayPassedThisYear) {
        age -= 1;
    }

    // Age limit check
    const maxAge = data.species === Species.Cat ? CA_MAX_AGE_CAT : CA_MAX_AGE_DOG;
    if (data.species && (data.species === Species.Cat ? age >= CA_MAX_AGE_CAT : age >= CA_MAX_AGE_DOG)) {
        const speciesName = data.species === Species.Cat ? "Cat" : "Dog";
        return {
            success: false,
            error: `For ${maxAge}+ year-old ${speciesName.toLowerCase()}s, please call ${UIUtils.formatPhone(Strings.PTZ_CA.PHONE_NUMBER)} for a specialized quote.`,
            field: "birthYear"
        };
    }

    return { success: true };
};

// Define the base shape without refinements
const BasePetFields = {
    ...PetSchema.shape,
    age: z.number().optional(),
    birthMonth: z.string().min(1, Strings.ERRORS.REQUIRED),
    birthYear: z.string().min(4, Strings.ERRORS.REQUIRED)
};

// Create the base object schema
const BasePetObjectSchema = z.object(BasePetFields);

// Create two versions: one with refinements and one without
const BasePetSchema = BasePetObjectSchema.superRefine((data, ctx) => {
    const result = validatePetAge(data);
    if (!result.success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: result.error,
            path: [result.field]
        });
    }
});

// Create a partial version using the object schema before refinements
const PartialBasePetSchema = BasePetObjectSchema.partial().superRefine((data, ctx) => {
    const result = validatePetAge(data);
    if (!result.success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: result.error,
            path: [result.field]
        });
    }
});

export const CaPetPoliciesSchema = z.object({
    policies: z.array(BasePetSchema)
});

export const PartialCaPetPoliciesSchema = z.object({
    policies: z.array(PartialBasePetSchema)
});

export const PolicyStepSchema = z.object({
    email: z.string().min(1, Strings.ERRORS.REQUIRED).max(50, Strings.ERRORS.EMAIL.MAX).email(Strings.ERRORS.EMAIL_INVALID),
    firstName: FirstNameSchema,
    lastName: LastNameSchema,
    ratingZipcode: z
        .string()
        .refine(value => value.length > 0, { message: Strings.ERRORS.REQUIRED })
        .refine(value => RegexUtils.postalCodeCARegex.test(value), { message: Strings.ERRORS.POSTAL_CODE_INVALID }),
    policies: z.array(BasePetSchema),
    marketing: MarketingSchema,
    phone: z
        .string()
        .refine(value => value === "" || RegexUtils.phoneRegex.test(value), Strings.ERRORS.PHONE)
        .optional(),
    extra: z.object({
        policyStepConsented: z.coerce.boolean().refine(value => value === true, {
            message: "Please agree to contact consent"
        })
    })
});

export const PartialPolicyStepSchema = PolicyStepSchema.extend({
    policies: z.array(PartialBasePetSchema)
}).partial();

export type PartialPolicyStepType = z.infer<typeof PartialPolicyStepSchema>;
export type PolicyStepType = z.infer<typeof PolicyStepSchema>;
export type CaPetPolicies = z.infer<typeof CaPetPoliciesSchema>;
export type PartialCaPetPolicies = z.infer<typeof PartialCaPetPoliciesSchema>;

// Checkout step form: holds the fields the step's RHF context manages (billing frequency for now).
// All-optional so it never blocks return-to-quote routing; payment itself is owned by the Chase iframe.
export const CaCheckoutStepSchema = z.object({
    billingInfo: z
        .object({
            frequency: z.enum(["monthly", "yearly"]).optional()
        })
        .optional()
});

export type CaCheckoutStepType = z.infer<typeof CaCheckoutStepSchema>;

// Mailing address collected on the CA checkout step when the customer opts OUT of paperless.
// Field rules mirror the shared AddressSchema (Quote.interface.ts) but the postal code is restricted
// to a Canadian format. Validated in useCheckoutValidation (via safeParse) before payment unlocks;
// on save it's written to quote.mailingAddress.
export const CaMailingAddressSchema = z.object({
    street1: z
        .string()
        .transform(value => value.trim().replace(/\s+/g, " "))
        .refine(value => value.length > 0, { message: Strings.ERRORS.REQUIRED })
        .refine(value => value.length <= 50, { message: Strings.ERRORS.ADDRESS_MAX })
        .refine(value => RegexUtils.hasLettersRegex.test(value), { message: Strings.ERRORS.ADDRESS_CHARS }),
    street2: z.string().max(50, Strings.ERRORS.ADDRESS_MAX).optional(),
    city: z
        .string()
        .transform(value => value.trim().replace(/\s+/g, " "))
        .refine(value => value.length > 0, { message: Strings.ERRORS.REQUIRED })
        .refine(value => value.length <= 50, { message: Strings.ERRORS.CITY_MAX })
        .refine(value => RegexUtils.hasLettersRegex.test(value), { message: Strings.ERRORS.CITY_CHARS }),
    // Province code sourced from the CA_PROVINCES select, so only a presence check is needed.
    state: z.string().refine(value => value.length > 0, { message: Strings.ERRORS.REQUIRED }),
    zipCode: z
        .string()
        .refine(value => value.length > 0, { message: Strings.ERRORS.REQUIRED })
        .refine(value => RegexUtils.postalCodeCARegex.test(value), { message: Strings.ERRORS.POSTAL_CODE_INVALID })
});

export type CaMailingAddress = z.infer<typeof CaMailingAddressSchema>;

// Combined validation for the fields the CA checkout step collects before the Chase iframe unlocks
// (phone, terms acceptance, paperless preference, and — when paperless is off — the mailing address).
// Single source of truth for both the per-field error messages and the "ready to persist / unlock
// payment" gate in useCheckoutValidation. The `address` members are plain strings here; the real
// address rules live in CaMailingAddressSchema and are applied via superRefine only when NOT paperless,
// so the address fields never block the gate while the customer is opted into paperless.
export const CaCheckoutFieldsSchema = z
    .object({
        phone: z.string().refine(value => RegexUtils.phoneRegex.test(value), { message: Strings.ERRORS.PHONE }),
        termsConsent: z.boolean().refine(value => value === true, { message: Strings.ERRORS.CONTACT_CONSENT }),
        paperless: z.boolean(),
        address: z.object({
            street1: z.string(),
            street2: z.string(),
            city: z.string(),
            state: z.string(),
            zipCode: z.string()
        })
    })
    .superRefine((value, ctx) => {
        if (value.paperless) return;
        const result = CaMailingAddressSchema.safeParse({
            street1: value.address.street1,
            street2: value.address.street2 === "" ? undefined : value.address.street2,
            city: value.address.city,
            state: value.address.state,
            zipCode: value.address.zipCode
        });
        if (!result.success) {
            for (const issue of result.error.issues) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message, path: ["address", ...issue.path] });
            }
        }
    });

export type CaCheckoutFields = z.infer<typeof CaCheckoutFieldsSchema>;
